import { SearchMd, XClose } from "@untitledui/icons";
import { type ComponentPropsWithoutRef, forwardRef, useId } from "react";
import { Icon } from "../Icon";
import { IconButton } from "../IconButton";
import styles from "./SearchBox.module.css";

/**
 * SearchBox — pill-shaped search input primitive.
 *
 * Matches the Figma SearchBox component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1011:1054). Three states (default /
 * Active / Filled) × 2 themes (light / dark). The Active state's
 * blinking caret in Figma is the native browser caret — no extra
 * work for us, the `<input>` handles it on focus automatically.
 *
 * This is a STANDALONE primitive, not an Input variant. The shape
 * (pill `--radius-round`), affordances (no visible label, inline
 * clear button), and visual language (command-palette style on
 * `--bg-secondary`, no border) all differ from Input. Bolting these
 * onto Input as a search variant would balloon Input's API for one
 * use case.
 *
 * The primitive is **controlled** — consumers own `value` + handle
 * `onChange`. This matches React's recommended pattern for
 * primitives that need to coordinate state with sibling components
 * (clear button visibility derives from `value.length`).
 *
 * Clear button is only rendered when `value.length > 0` AND
 * `onClear` is provided AND the input is not disabled. This makes
 * the clear behaviour opt-in: consumers who want the X button must
 * wire up the clear handler. Disabled SearchBoxes never show the X
 * regardless — clicking a clear on a disabled control is a footgun.
 *
 * Layout: the contents are wrapped in a `<label htmlFor>` (with an
 * auto-generated id from `useId()`, falling back to a consumer-passed
 * `id`) so clicking anywhere on the pill — including the padding
 * dead-zone around the magnifier — focuses the input. Native
 * `<label>` behaviour also handles disabled correctly: clicks on a
 * label associated with a disabled input do nothing, no JS needed.
 *
 * Accessibility:
 *   - The `label` prop is REQUIRED and becomes `aria-label` on the
 *     input. There's no visible label text in the Figma spec, so
 *     this is the only accessible name source.
 *   - `aria-label`, `role`, and `size` are in the Omit list to
 *     prevent silent overrides via spread props (`size` is the
 *     legacy <input size="N"> character-width attribute that would
 *     quietly break the fluid pill layout if it leaked through).
 *   - Spread order: `{...rest}` BEFORE managed attrs so managed
 *     attrs always win even if the Omit ever leaks.
 *   - The clear button is a real `<button type="button">` via the
 *     IconButton primitive; its aria-label comes from the `clearLabel`
 *     prop (default `"Clear search"`).
 *   - The wrapping `<label>` is purely a click-to-focus affordance;
 *     it has no visible text, so it does NOT show up in the
 *     accessibility tree as a separate label (the input already has
 *     an explicit `aria-label`, which takes precedence per ARIA).
 */

export type SearchBoxProps = {
  /** Controlled input value. */
  value: string;
  /** Called whenever the input value changes. Receives the new string. */
  onChange: (value: string) => void;
  /** Called when the user clicks the clear (×) button. If omitted, the
   *  clear button is never rendered. */
  onClear?: () => void;
  /** Required accessible label. There's no visible <label> text, so this
   *  becomes `aria-label` on the input. */
  label: string;
  /** aria-label for the inline clear (×) button. Defaults to
   *  `"Clear search"` for back-compat; hosts should thread a localized
   *  value. Distinct from `label` (the search input's own aria-label). */
  clearLabel?: string;
  /** Placeholder text. Defaults to `"Search"`. */
  placeholder?: string;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"input">,
  "value" | "onChange" | "type" | "className" | "aria-label" | "placeholder" | "role" | "size"
>;

export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(function SearchBox(
  {
    value,
    onChange,
    onClear,
    label,
    clearLabel = "Clear search",
    placeholder = "Search",
    disabled,
    id,
    className,
    ...rest
  },
  ref
) {
  // Stable id for the label↔input association. Use the consumer-supplied
  // id if provided, otherwise auto-generate one. Same pattern Input uses.
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const containerClasses = [styles.container, disabled ? styles.containerDisabled : null, className]
    .filter(Boolean)
    .join(" ");
  // Clear button only renders when (a) there's something to clear,
  // (b) the consumer provided an onClear handler, AND (c) the input
  // is not disabled. Rendering an interactive X on a disabled control
  // would let users mutate state through a "disabled" UI.
  const showClear = value.length > 0 && onClear !== undefined && !disabled;

  return (
    // The label wraps the entire pill so clicking anywhere — including
    // the padding around the magnifier — focuses the input. The label
    // has no visible text content; aria-label on the input is the
    // accessible name source.
    <label htmlFor={inputId} className={containerClasses}>
      <Icon as={SearchMd} size="sm" tone="inherit" className={styles.searchIcon} />
      <input
        {...rest}
        ref={ref}
        id={inputId}
        type="search"
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
      />
      {showClear && (
        <IconButton
          icon={XClose}
          label={clearLabel}
          variant="default"
          size="s"
          onClick={onClear}
          className={styles.clearButton}
        />
      )}
    </label>
  );
});
